import React, { FC, useCallback, useState, useEffect } from "react";
import styled from "styled-components";
import { CartItem } from "./CartItem";
import { OrderSummary } from "../RockstarOrderSummary/OrderSummary";
import { Alert } from "../alert/Alert";
import { Button } from "@pepdirect/ui/button";
import { Divider } from "../divider/Divider";
import { Heading } from "@pepdirect/ui/heading";
import { logCartLineItemQtyChangedEvent } from "@pepdirect/helpers/analyticsLogger";
import { formatDollars } from "../../helpers/currency";
import { CartFragment, CartItemUpdateDto } from "@pepdirect/shared/types";
import { Device } from "../../helpers/SharedHelpers/screenSizes";
import { DatoProductFragment } from "services/datocms/generated";

const StyledCart = styled.section`
  padding: 40px 20px 60px;
  width: 100%;

  @media ${Device.notMobile} {
    padding: 40px 30px 80px;
    margin: auto;
  }
`;

const StyledHeading = styled(Heading)`
  @media ${Device.mobile} {
    text-align: center;
  }
`;

const TotalInHeading = styled.span`
  @media ${Device.notMobile} {
    display: none;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 20px;

  @media ${Device.mobile} {
    flex-direction: column-reverse;
  }
`;

const CartItems = styled.div`
  width: calc(100% - 375px);
  margin-bottom: 20px;
  box-sizing: border-box;
  vertical-align: top;
  font-family: "AktivGrotesk Ex Black Italic", sans-serif;
  text-transform: uppercase;

  @media ${Device.mobile} {
    width: 100%;
  }

  @media ${Device.tablet} {
    width: calc(100% - 300px);
  }
`;

const StyledAlert = styled.div`
  padding-bottom: 20px;
`;

const OrderSummaryDesktop = styled.div`
  padding-left: 20px;
  width: 375px;
  border-radius: 3px;
  box-sizing: border-box;
  vertical-align: top;

  @media ${Device.tablet} {
    width: 300px;
  }

  @media ${Device.mobile} {
    display: none;
  }
`;

const OrderSummaryMobile = styled.div`
  @media ${Device.notMobile} {
    display: none;
  }

  @media ${Device.mobile} {
    width: 100%;
    margin-top: 0;
    padding: 0;
  }
`;

const ButtonMobile = styled.div`
  @media ${Device.mobile} {
    margin-top: 20px;
  }

  @media ${Device.notMobile} {
    display: none;
  }
`;

const ButtonNotMobile = styled.div`
  margin-top: 20px;
  text-align: right;

  button {
    width: 320px;
    display: inline-block;

    @media ${Device.mobile} {
      display: none;
    }
  }
`;

export interface CartProps {
  cmsProducts: DatoProductFragment[];
  size: number | undefined;
  cart: CartFragment | null;
  blockedItems?: string[];
  subtotal: number;
  total: number;
  currentUserId: string | null;
  maxCartQty: number;
  infoText: string;
  productsImgCdnEndpoint: string;
  errors: string[];
  freeShipping: boolean;
  isButtonDisabled: boolean;
  updateCartItem: (_dto: CartItemUpdateDto) => Promise<void>;
  goToCheckout: () => void;
  itemImgCdn?: string;
}

export const Cart: FC<CartProps> = ({
  cmsProducts,
  size,
  cart,
  blockedItems,
  subtotal,
  total,
  currentUserId,
  maxCartQty,
  infoText,
  productsImgCdnEndpoint,
  errors,
  freeShipping,
  isButtonDisabled,
  updateCartItem,
  goToCheckout,
}) => {
  const [hasBlockedItems, setHasBlockedItems] = useState(false);
  const [headingText, setHeadingText] = useState("Your cart");

  useEffect(() => {
    if (size) {
      setHeadingText(`Your cart (${size} ${size > 1 ? "items" : "item"})`);
    } else {
      setHeadingText("Your cart");
    }
  }, [size]);

  useEffect(() => {
    if (cart && blockedItems) {
      setHasBlockedItems(
        cart?.items?.some((cartItem) =>
          blockedItems.includes(cartItem.product.id)
        )
      );
    }
  }, [cart, blockedItems]);

  const getBlockedItem = useCallback(
    (id: string) => {
      if (blockedItems && blockedItems.includes(id)) {
        return {
          id,
          isZipError: true,
        };
      }
      return undefined;
    },
    [blockedItems]
  );

  return (
    <StyledCart>
      <StyledHeading level="h1" size="xl">
        {headingText}
        <TotalInHeading> {formatDollars(total)}</TotalInHeading>
      </StyledHeading>

      <ButtonMobile data-testid="cart-button-mobile">
        <Button
          type="secondary"
          fullWidth
          onClick={goToCheckout}
          disabled={isButtonDisabled || hasBlockedItems}
          style={{
            background: "#CF9C43",
            color: "black",
            border: "none",
            borderRadius: 0,
          }}
        >
          Proceed To Checkout
        </Button>
      </ButtonMobile>

      <Content>
        <CartItems>
          {errors.includes("lowStock") && (
            <StyledAlert data-testid="cart-alert-low-stock">
              <Alert type="error">
                There are items in your cart where the quantity selected is more
                than we have in stock. Please adjust or remove these items to
                continue to checkout.
              </Alert>
            </StyledAlert>
          )}

          {errors.includes("noStock") && (
            <StyledAlert data-testid="cart-alert-no-stock">
              <Alert type="error">
                There are items in your cart that are <u>sold out</u>. Please
                remove them to continue to checkout.
              </Alert>
            </StyledAlert>
          )}

          {infoText && (
            <StyledAlert data-testid="cart-alert-info">
              <Alert>{infoText}</Alert>
            </StyledAlert>
          )}

          <Divider />

          {cart?.items?.map((cartItem, index) => {
            return (
              <CartItem
                {...cartItem}
                cart={cart}
                maxCartQty={maxCartQty}
                itemImgSrc={cartItem.product.imgUrl || ""}
                currentUserId={currentUserId}
                blockedItem={getBlockedItem(cartItem.product.id)}
                updateCartItem={updateCartItem}
                logCartLineItemQtyChangedEvent={logCartLineItemQtyChangedEvent}
                key={`cart-item-${index}`}
                itemImgCdn={productsImgCdnEndpoint}
                isLink={true}
                cmsProducts={cmsProducts}
              />
            );
          })}

          <OrderSummaryMobile data-testid="cart-order-summary-mobile">
            <OrderSummary
              cart={cart}
              subtotal={subtotal}
              total={total}
              freeShipping={freeShipping}
              isButtonDisabled={isButtonDisabled || hasBlockedItems}
              goToCheckout={goToCheckout}
            />
          </OrderSummaryMobile>

          {!!cart && cart.items.length > 1 && (
            <ButtonNotMobile data-testid="cart-button-not-mobile">
              <Button
                type="secondary"
                fullWidth
                onClick={goToCheckout}
                disabled={isButtonDisabled || hasBlockedItems}
                style={{
                  background: "#CF9C43",
                  color: "black",
                  border: "none",
                  borderRadius: 0,
                }}
              >
                Proceed To Checkout
              </Button>
            </ButtonNotMobile>
          )}
        </CartItems>

        <OrderSummaryDesktop data-testid="cart-order-summary-desktop">
          <OrderSummary
            cart={cart}
            subtotal={subtotal}
            total={total}
            freeShipping={freeShipping}
            isButtonDisabled={isButtonDisabled || hasBlockedItems}
            goToCheckout={goToCheckout}
          />
        </OrderSummaryDesktop>
      </Content>
    </StyledCart>
  );
};
